import React, { useEffect, useState } from "react";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import { Controller } from "react-hook-form";
import TextField from "@mui/material/TextField";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

// Updated MenuProps with more comprehensive event blocking
const MenuProps = {
  autoFocus: false,
  disableAutoFocusItem: true, // Add this to disable auto focus on menu items
  PaperProps: {
    onKeyDown: (e) => {
      // Block navigation keys
      if (["ArrowUp", "ArrowDown", "Home", "End"].includes(e.key)) {
        e.stopPropagation();
        e.preventDefault();
      }
    },
    style: {
      maxHeight: 48 * 4.5 + 8,
      width: 425,
      backgroundColor: "white",
    },
  },
  // Add MenuListProps to intercept events at the MenuList level
  MenuListProps: {
    onKeyDown: (e) => {
      if (["ArrowUp", "ArrowDown", "Home", "End"].includes(e.key)) {
        e.stopPropagation();
        e.preventDefault();
      }
    },
    // Disable auto focus behavior
    autoFocusItem: false,
    // Disable typeahead
    disableListWrap: true,
  },
  // Prevent the menu from auto-focusing
  disablePortal: true,
  // Disable auto-focus when menu opens
  disableAutoFocus: true,
};

const MultipleSelectCheckmarks = ({
  list_items,
  preSelectedItems = [],
  onChangeHandler,
  label = "",
  disabled = false,
  disableMultiple = false,
  control,
  name,
  rules,
  enableSearch = false,
}) => {
  const names = list_items || [];
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedNames, setSelectedNames] = useState(() => {
    return [...preSelectedItems];
  });
  // Add a ref to access the select element
  const selectRef = React.useRef(null);

  // Sync state when preSelectedItems change
  useEffect(() => {
    setSelectedNames(preSelectedItems);
  }, [preSelectedItems]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  const filteredNames = names.filter((name) =>
    name.toLowerCase().includes(searchQuery)
  );

  const handleChange = (event) => {
    let value = event.target.value;

    if (typeof value === "string") {
      value = value.split(",");
    }

    if (value.includes("Select All")) {
      const allSelected = selectedNames.length === names.length;
      const newSelection = allSelected ? [] : [...names];
      setSelectedNames(newSelection);
      onChangeHandler(newSelection);
      return;
    }

    setSelectedNames(value);
    onChangeHandler(value);
  };

  // Function to handle key down events on the select component
  const handleSelectKeyDown = (e) => {
    // Prevent default behavior for navigation keys
    if (["ArrowUp", "ArrowDown", "Home", "End"].includes(e.key)) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <FormControl sx={{ width: "100%" }}>
          <InputLabel id="multiple-checkbox-label">{label}</InputLabel>
          <Select
            ref={selectRef}
            labelId="multiple-checkbox-label"
            id="multiple-checkbox"
            multiple={!disableMultiple}
            disableAutoFocusItem
            value={selectedNames}
            onChange={(e) => {
              handleChange(e);
              field.onChange(e);
            }}
            // Add onKeyDown handler to the Select component
            onKeyDown={handleSelectKeyDown}
            input={
              <OutlinedInput
                label={label}
                sx={{ height: "45px", width: "100%", backgroundColor: "white" }}
                // Add onKeyDown handler to the input as well
                onKeyDown={handleSelectKeyDown}
              />
            }
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <em>Select</em>;
              }
              return selected.join(", ");
            }}
            MenuProps={MenuProps}
            disabled={disabled}
            // Disable the typeahead feature
            native={false}
            // Add these props to further disable auto-focus behavior
            SelectDisplayProps={{
              onKeyDown: handleSelectKeyDown,
            }}
          >
            {enableSearch && (
              <TextField
                size="small"
                placeholder="Search..."
                fullWidth
                onChange={handleSearchChange}
                value={searchQuery}
                sx={{ padding: 1, margin: "5px 10px" }}
                // Stop propagation of keyboard events from the search field
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
              />
            )}
            {/* Select All Option */}
            {!disableMultiple && (
              <MenuItem
                value="Select All"
                // Disable auto-focus on this item
                autoFocus={false}
              >
                <Checkbox checked={selectedNames.length === names.length} />
                <ListItemText primary="Select All" />
              </MenuItem>
            )}

            {filteredNames.map((name) => (
              <MenuItem
                key={name}
                value={name}
                // Disable auto-focus on each item
                autoFocus={false}
              >
                <Checkbox checked={selectedNames.includes(name)} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    />
  );
};

export default MultipleSelectCheckmarks;
